import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AutomatedRemediationPanel = ({ remediationLog, onExecuteRemediation }) => {
  const remediationProtocols = [
    {
      id: 'service_restart',
      name: 'Service Restart',
      description: 'Restart affected service to recover from errors',
      icon: 'RotateCw',
      color: 'blue',
      useCase: 'Memory leaks, hung processes'
    },
    {
      id: 'load_balancing_adjustment',
      name: 'Load Balancing Adjustment',
      description: 'Redistribute traffic across available servers',
      icon: 'Activity',
      color: 'purple',
      useCase: 'High traffic, server overload'
    },
    {
      id: 'failover_activation',
      name: 'Failover Activation',
      description: 'Switch to backup system automatically',
      icon: 'Shield',
      color: 'red',
      useCase: 'Primary system failure'
    },
    {
      id: 'cache_flush',
      name: 'Cache Flush',
      description: 'Clear application cache to resolve stale data',
      icon: 'RefreshCw',
      color: 'green',
      useCase: 'Stale data, cache corruption'
    },
    {
      id: 'connection_pool_reset',
      name: 'Connection Pool Reset',
      description: 'Reset database connection pool',
      icon: 'Database',
      color: 'orange',
      useCase: 'Connection exhaustion, deadlocks'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
      purple: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100',
      red: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
      green: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
      orange: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100'
    };
    return colors?.[color] || 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Wrench" className="w-5 h-5 text-blue-600" />
          Automated Remediation Protocols
        </h3>
        <p className="text-sm text-gray-600 mt-1">Execute immediate responses for common infrastructure issues</p>
      </div>
      {/* Remediation Protocols */}
      <div>
        <div className="text-sm font-medium text-gray-700 mb-3">Available Protocols</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {remediationProtocols?.map(protocol => (
            <div
              key={protocol?.id}
              className={`border rounded-lg p-4 transition-all ${
                getColorClasses(protocol?.color)
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                    <Icon name={protocol?.icon} className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{protocol?.name}</div>
                    <div className="text-xs text-gray-600 mt-0.5">{protocol?.useCase}</div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">{protocol?.description}</p>
              <Button
                onClick={() => onExecuteRemediation(protocol?.id, { serviceName: 'auto-detected' })}
                size="sm"
                className="w-full"
              >
                <Icon name="Play" className="w-4 h-4 mr-2" />
                Execute Protocol
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Execution Log */}
      <div>
        <div className="text-sm font-medium text-gray-700 mb-3">Execution History</div>
        {remediationLog?.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {remediationLog?.map((log, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    log?.result?.success ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <Icon
                      name={log?.result?.success ? 'CheckCircle' : 'XCircle'}
                      className={`w-4 h-4 ${
                        log?.result?.success ? 'text-green-600' : 'text-red-600'
                      }`}
                    />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {log?.protocol?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </div>
                    <div className="text-xs text-gray-600">{log?.result?.message}</div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(log?.timestamp)?.toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <Icon name="Clock" className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No remediation protocols executed yet</p>
          </div>
        )}
      </div>
      {/* SLA Compliance */}
      <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-900">SLA Compliance Status</div>
          <span className="text-2xl font-bold text-green-600">99.95%</span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-gray-600 mb-1">Uptime Target</div>
            <div className="font-medium text-gray-900">99.9%</div>
          </div>
          <div>
            <div className="text-gray-600 mb-1">Incident Resolution</div>
            <div className="font-medium text-gray-900">45 min avg</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomatedRemediationPanel;
